import React from 'react';
import {useIntl} from 'react-intl';
import {IconText, Table} from '@/web-components';
import ProjectLoader, {projectLoaderQuery} from './ProjectLoader';
import {
	canExpandDeliverableRow,
	filtersToSearchQuery,
	getBreakdownDeliverableId,
	getDeliverableName,
} from '../ProjectPortfolioReportUtils';
import ProjectPortfolioPeriodDownload, {projectPortfolioPeriodDownloadQuery} from './ProjectPortfolioPeriodDownload';
import {NO_ID} from '../../../../constants';
import {MODAL_TYPE, showModal} from '../../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {LoadMore} from '../../../loaders/LoadMore';
import ProjectPortfolioPeriodRow from './ProjectPortfolioPeriodRow';
import Util from '../../../../forecast-app/shared/util/util';
import DeprecatedProjectIndicatorJS from '../../../../forecast-app/shared/components/project-indicator/js/DeprecatedProjectIndicatorJS';
import DeliverableLoader, {deliverableLoaderQuery} from './DeliverableLoader';
import PhaseRoleCascade from './PhaseRoleCascade';

const ProjectPortfolioPeriodProjectRows = ({
	setCsvDownloadFunction,
	pageSize,
	startDate,
	endDate,
	filters,
	boolEyeOptions,
	levelProps,
	projectGroupId,
	programId,
	clientId,
	footerRowData,
	useWinProbability,
}) => {
	const intl = useIntl();

	const addedFilters = [];
	if (projectGroupId) {
		const isNoConnectedProject = Util.getUUIdFromBase64String(projectGroupId) === NO_ID;
		addedFilters.push({key: 'projectGroup', operator: 'IS', value: isNoConnectedProject ? [null] : projectGroupId});
	}
	if (programId) {
		const isNoProgram = Util.getUUIdFromBase64String(programId) === NO_ID;
		addedFilters.push({key: 'program', operator: 'IS', value: isNoProgram ? [null] : programId});
	}
	if (clientId) {
		const isNoClient = Util.getUUIdFromBase64String(clientId) === NO_ID;
		addedFilters.push({key: 'client', operator: 'IS', value: isNoClient ? [null] : clientId});
	}

	return (
		<LoadMore
			key="query-render-project"
			query={projectLoaderQuery}
			variables={{
				searchQuery: filtersToSearchQuery(filters, addedFilters),
				startYear: startDate ? startDate.year() : undefined,
				startMonth: startDate ? startDate.month() + 1 : undefined,
				startDay: startDate ? startDate.date() : undefined,
				endYear: endDate ? endDate.year() : undefined,
				endMonth: endDate ? endDate.month() + 1 : undefined,
				endDay: endDate ? endDate.date() : undefined,
				pageSize: pageSize,
				ignoreBaselineWinChance: !useWinProbability,
				excludeOpportunityActuals: true,
			}}
			loader={<ProjectLoader programId={programId} />}
		>
			{({data, loadMore}) => {
				if (!projectGroupId && !programId && !clientId) {
					setCsvDownloadFunction(() => {
						showModal({
							type: MODAL_TYPE.DOWNLOADING_CSV_DATA,
							componentName: 'project_portfolio_period_project_csv_loader',
							query: projectPortfolioPeriodDownloadQuery,
							variables: {
								searchQuery: filtersToSearchQuery(filters),
								startYear: startDate ? startDate.year() : undefined,
								startMonth: startDate ? startDate.month() + 1 : undefined,
								startDay: startDate ? startDate.date() : undefined,
								endYear: endDate ? endDate.year() : undefined,
								endMonth: endDate ? endDate.month() + 1 : undefined,
								endDay: endDate ? endDate.date() : undefined,
								pageSize: 1000000,
								ignoreBaselineWinChance: !useWinProbability,
								excludeOpportunityActuals: true,
							},
							createCsvDownload: <ProjectPortfolioPeriodDownload />,
							startDate,
							endDate,
							boolEyeOptions,
						});
					});
				}
				if (footerRowData) {
					data.rows.push({...footerRowData, isFooter: true});
				}
				if (data.programUnallocatedRevenue && !filters?.project?.length < 1) {
					data.rows.push({
						name: 'Unallocated program revenue',
						currency: data.currency,
						financialNumbers: {
							totalRevenueRecognition: data.programUnallocatedRevenue,
						},
						isFooter: true,
					});
				}
				return (
					<Table.Rows data={data} canExpand={true} loadMore={loadMore} autoload={true} {...levelProps}>
						{({rowData, tableColumnsProps, expanded, nextLevelProps: phaseLevelProps}) => {
							const fullAccessToProject = rowData?.project?.fullAccessToProject;
							const withProject = data => ({...data, project: rowData.project});

							return !rowData.isFooter ? (
								<>
									<ProjectPortfolioPeriodRow
										financialNumbers={rowData.financialNumbers}
										rowData={rowData}
										tableColumnProps={tableColumnsProps}
										canExpand={true}
										headerColumn={
											<IconText
												key={rowData.project.id}
												text={
													fullAccessToProject
														? rowData.project.name
														: intl.formatMessage({id: 'common.no_project_access'})
												}
											>
												<DeprecatedProjectIndicatorJS
													project={rowData.project}
													disableLink={rowData.project.readOnlyAccess}
												/>
											</IconText>
										}
										useWinProbability={useWinProbability}
									/>
									{expanded &&
										(rowData.project.useDeliverables ? (
											<LoadMore
												key="query-render-deliverable"
												query={deliverableLoaderQuery}
												loader={<DeliverableLoader />}
												variables={{
													projectId: rowData.project.id,
													startYear: startDate ? startDate.year() : undefined,
													startMonth: startDate ? startDate.month() + 1 : undefined,
													startDay: startDate ? startDate.date() : undefined,
													endYear: endDate ? endDate.year() : undefined,
													endMonth: endDate ? endDate.month() + 1 : undefined,
													endDay: endDate ? endDate.date() : undefined,
													ignoreBaselineWinChance: !useWinProbability,
													excludeOpportunityActuals: true,
												}}
											>
												{({data: deliverableBreakdownData}) => {
													return (
														<Table.Rows
															data={deliverableBreakdownData}
															{...phaseLevelProps}
															canExpand={true}
														>
															{({
																rowData: deliverableRowData,
																tableColumnsProps: deliverableTableColumnProps,
																expanded: deliverableExpanded,
																nextLevelProps: nestedPhaseLevelProps,
															}) => {
																const deliverableId =
																	getBreakdownDeliverableId(deliverableRowData);
																return (
																	<>
																		<ProjectPortfolioPeriodRow
																			financialNumbers={
																				deliverableRowData.financialNumbers
																			}
																			rowData={withProject(deliverableRowData)}
																			tableColumnProps={deliverableTableColumnProps}
																			headerColumn={getDeliverableName(
																				intl,
																				deliverableRowData
																			)}
																			canExpand={canExpandDeliverableRow(
																				deliverableRowData
																			)}
																			useWinProbability={useWinProbability}
																		/>
																		{deliverableExpanded && (
																			<PhaseRoleCascade
																				intl={intl}
																				projectId={rowData.project.id}
																				deliverableId={deliverableId}
																				startDate={startDate}
																				endDate={endDate}
																				useWinProbability={useWinProbability}
																				levelProps={nestedPhaseLevelProps}
																				withProject={withProject}
																			/>
																		)}
																	</>
																);
															}}
														</Table.Rows>
													);
												}}
											</LoadMore>
										) : (
											<PhaseRoleCascade
												intl={intl}
												projectId={rowData.project.id}
												deliverableId={undefined}
												startDate={startDate}
												endDate={endDate}
												useWinProbability={useWinProbability}
												levelProps={phaseLevelProps}
												withProject={withProject}
											/>
										))}
								</>
							) : (
								<ProjectPortfolioPeriodRow
									financialNumbers={rowData.financialNumbers}
									rowData={rowData}
									tableColumnProps={tableColumnsProps}
									canExpand={false}
									headerColumn={rowData.name || ''}
									useWinProbability={useWinProbability}
								/>
							);
						}}
					</Table.Rows>
				);
			}}
		</LoadMore>
	);
};

export default ProjectPortfolioPeriodProjectRows;
